import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { bulkUploadDynamicPricing, syncDpMeta } from "../../../core/actionCreators/dynamicPricingActionCreator";
import { toast } from "react-hot-toast";
import FileDownloadLineIcon from "remixicon-react/FileDownloadLineIcon";
import DateRangePicker from "../common/DateRangePicker";
import moment from "moment";
import { getDynamicPricingHistoryService } from "../../../core/services/dynamicPricingService";

// Reusable styling and visibility flags (DRY and Open/Closed principles)
const PRIMARY_BUTTON_CLASS = "h-9 px-3 bg-blue-500 text-white text-sm font-semibold rounded shadow-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-opacity-75 disabled:opacity-50 whitespace-nowrap flex-shrink-0 inline-flex items-center justify-center";
const SHOW_SYNC_META_BUTTON = false;

const DynamicPricingSubHeader = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [showUploadResultModal, setShowUploadResultModal] = useState(false);

  const [dateRange, setDateRange] = useState<[moment.Moment, moment.Moment]>([
    moment().subtract(6, 'days').startOf('day'),
    moment().endOf('day')
  ]);
  const [isDownloadingHistory, setIsDownloadingHistory] = useState(false);

  const navigateToCreateHandler = () => {
    navigate("/dynamic-pricing/create");
  };

  const handleBulkUploadClick = () => {
    fileInputRef.current?.click();
  };

  const downloadSampleFile = () => {
    const headers = "product_ref,category,product_name,min_gm_percentage,max_gm_percentage,amazon_competitiveness_percentage,flipkart_competitiveness_percentage,ib_competitiveness_percentage,Toolsvilla_Compititiveness_Percentage,tatacliq_competitiveness_percentage,croma_competitiveness_percentage,reliance_competitiveness_percentage,dynamic_pricing_enabled,primary_competitor";
    const sampleRow1 = 'msnvk13e7wp891,office stationery & supplies,"Brother DCP-T730DW All-in-One Color Ink Tank Printer, Wi-Fi, ADF & Duplex",5,7,5,5,,,,,,true,amazon';
    const sampleRow2 = 'msn2vl9ywm3a4x,office stationery & supplies,"Canon Pixma MegaTank G2010 All-in-One Color Ink Tank Printer, USB",5,7,5,5,,,,,,true,flipkart';

    const csvContent = `${headers}\n${sampleRow1}\n${sampleRow2}`;
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "sample_dynamic_pricing.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error("Please upload a valid CSV file");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    formData.append("emailId", "test@gmail.com"); // Hardcoded email

    dispatch(bulkUploadDynamicPricing(formData, (success: boolean, resultData: any) => {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
        
        // Scenario 1 & 2: Success with upload statistics
        if (resultData?.status === true && resultData.data?.successCount !== undefined) {
            setUploadResult(resultData.data);
            setShowUploadResultModal(true);
        } 
        // Fallback for success without statistics
        else if (resultData?.status === true || success) {
            toast.success(resultData?.message || "Bulk upload successful.");
            setTimeout(() => {
                window.location.reload();
            }, 1000);
        } 
        // Scenarios 3, 4, 5: Failure
        else {
            const errorMsg = typeof resultData === 'string' 
                ? resultData 
                : (resultData?.message || resultData?.statusDescription || "Failed to bulk upload.");
            toast.error(errorMsg);
        }
    }));
  };

  const handleSyncClick = () => {
    setIsSyncing(true);
    dispatch(syncDpMeta((success: boolean) => {
        setIsSyncing(false);
    }));
  };

  const handleDownloadHistoryClick = async () => {
    try {
      setIsDownloadingHistory(true);
      const startDateStr = dateRange[0].format("YYYY-MM-DD HH:mm:ss");
      const endDateStr = dateRange[1].format("YYYY-MM-DD HH:mm:ss");
      
      const response = await getDynamicPricingHistoryService(startDateStr, endDateStr);
      const data = response.data;
      
      if (data?.status === true && data?.data?.records) {
        const records = data.data.records;
        if (records.length === 0) {
          toast.error("No history records found for the selected date range.");
          setIsDownloadingHistory(false);
          return;
        }
        
        const headers = "id,productRef,category,minPrice,maxPrice,amazonPrice,flipkartPrice,ibPrice,ToolsvillaPrice,tataCliqPrice,cromaPrice,reliancePrice,dynamicPricingEnabled,primaryCompetitor,action,createdAt,createdBy";
        
        const csvRows = records.map((record: any) => {
          return [
            record.id || '',
            record.productRef || '',
            record.category || '',
            record.minPrice ?? '',
            record.maxPrice ?? '',
            record.amazonPrice ?? '',
            record.flipkartPrice ?? '',
            record.ibPrice ?? '',
            record.ToolsvillaPrice ?? '',
            record.tataCliqPrice ?? '',
            record.cromaPrice ?? '',
            record.reliancePrice ?? '',
            record.dynamicPricingEnabled ?? '',
            record.primaryCompetitor || '',
            record.action || '',
            record.createdAt || '',
            record.createdBy || ''
          ].map((value) => `"${String(value ?? '').replace(/"/g, '""')}"`).join(',');
        });
        
        const csvContent = `${headers}\n${csvRows.join('\n')}`;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const safeStart = dateRange[0].format("YYYY-MM-DD");
        const safeEnd = dateRange[1].format("YYYY-MM-DD");
        link.setAttribute("download", `dynamic_pricing_history_${safeStart}_to_${safeEnd}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        toast.success("History downloaded successfully.");
      } else {
        toast.error(data?.message || "Failed to download history.");
      }
    } catch (err: any) {
      console.error("handleDownloadHistoryClick error:", err);
      toast.error(err?.response?.data?.message || "Something went wrong while fetching history.");
    } finally {
      setIsDownloadingHistory(false);
    }
  };

  return (
    <>
      <div className="p-4 flex items-center justify-between">
        <p className="text-xl font-bold text-[#43425D] whitespace-nowrap pr-4">DYNAMIC PRICING</p>

        <div className="flex flex-1 justify-end items-center gap-2">
          <DateRangePicker
            dateRange={dateRange}
            onApply={(range) => setDateRange(range)}
            buttonClassName="inline-flex items-center gap-1 h-9 px-2 bg-white border border-gray-300 text-gray-700 rounded shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm font-medium whitespace-nowrap flex-shrink-0"
          />
          <button
            type="button"
            onClick={handleDownloadHistoryClick}
            disabled={isDownloadingHistory}
            title="Download history based on selected date range"
            className="inline-flex items-center gap-1 h-9 px-2 border border-blue-300 text-blue-700 rounded hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium whitespace-nowrap disabled:opacity-50 flex-shrink-0"
          >
            <FileDownloadLineIcon size={14} />
            {isDownloadingHistory ? "Downloading..." : "History"}
          </button>
          
          <button
            type="button"
            onClick={downloadSampleFile}
            title="Download a sample CSV showing the columns required for bulk upload"
            className="inline-flex items-center gap-1 h-9 px-2 border border-green-300 text-green-700 rounded hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm font-medium whitespace-nowrap flex-shrink-0"
          >
            <FileDownloadLineIcon size={14} />
            Sample
          </button>

          <input
            type="file"
            accept=".csv"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={handleBulkUploadClick}
            disabled={isUploading}
            className={PRIMARY_BUTTON_CLASS}
          >
            {isUploading ? "Uploading..." : "Bulk Upload CSV"}
          </button>

          {SHOW_SYNC_META_BUTTON && (
            <button
              onClick={handleSyncClick}
              disabled={isSyncing}
              className="h-9 px-3 bg-orange-500 text-white text-sm font-semibold rounded shadow-md hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-opacity-75 disabled:opacity-50 whitespace-nowrap flex-shrink-0"
            >
              {isSyncing ? "Syncing..." : "Sync DP Meta"}
            </button>
          )}

          <button
            onClick={navigateToCreateHandler}
            className={PRIMARY_BUTTON_CLASS}
          >
            Add Mapping
          </button>
        </div>
      </div>

      {/* Bulk Upload Result Modal */}
      {showUploadResultModal && uploadResult && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
              <div className="relative p-6 border w-full max-w-2xl shadow-lg rounded-md bg-white">
                  <div className="flex justify-between items-center mb-6">
                      <h3 className="text-xl font-bold text-gray-900">Bulk Upload Results</h3>
                      <button type="button" aria-label="Close bulk upload result modal" onClick={() => setShowUploadResultModal(false)} className="text-gray-400 hover:text-gray-600">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-6">
                      <div className="bg-gray-50 p-4 rounded border text-center">
                          <div className="text-sm text-gray-500 font-medium">Total Records</div>
                          <div className="text-2xl font-bold text-gray-900">{(uploadResult.successCount || 0) + (uploadResult.failedCount || 0)}</div>
                      </div>
                      <div className="bg-green-50 p-4 rounded border border-green-200 text-center">
                          <div className="text-sm text-green-600 font-medium">Success</div>
                          <div className="text-2xl font-bold text-green-700">{uploadResult.successCount}</div>
                      </div>
                      <div className="bg-red-50 p-4 rounded border border-red-200 text-center">
                          <div className="text-sm text-red-600 font-medium">Failed</div>
                          <div className="text-2xl font-bold text-red-700">{uploadResult.failedCount}</div>
                      </div>
                  </div>

                  {uploadResult.failedCount > 0 && uploadResult.errors && (
                      <div className="mt-4">
                          <h4 className="text-md font-semibold text-gray-800 mb-2">Error Details</h4>
                          <div className="max-h-60 overflow-y-auto border rounded bg-gray-50">
                              <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-100">
                                      <tr>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">MSN</th>
                                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Error Message</th>
                                      </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                      {uploadResult.errors.map((err: any, idx: number) => (
                                          <tr key={idx}>
                                              <td className="px-4 py-2 text-sm text-gray-900">{err.rowNumber}</td>
                                              <td className="px-4 py-2 text-sm font-medium text-gray-900">{err.productRef}</td>
                                              <td className="px-4 py-2 text-sm text-red-600">{err.reason}</td>
                                          </tr>
                                      ))}
                                  </tbody>
                              </table>
                          </div>
                      </div>
                  )}

                  <div className="mt-6 flex justify-end">
                      <button
                          onClick={() => {
                              setShowUploadResultModal(false);
                              window.location.reload();
                          }}
                          className="px-4 py-2 bg-gray-800 text-white rounded hover:bg-gray-900"
                      >
                          Close & Reload
                      </button>
                  </div>
              </div>
          </div>
      )}
    </>
  );
};

export default DynamicPricingSubHeader;
